import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useJournalStore } from '../../../../stores/journalStore';

interface EditJournalNameModalProps {
    isOpen: boolean;
    onClose: () => void;
    journalId: string;
    currentName: string;
}

export const EditJournalNameModal: React.FC<EditJournalNameModalProps> = ({
    isOpen,
    onClose,
    journalId,
    currentName
}) => {
    const { t } = useTranslation('common');
    const [name, setName] = useState(currentName);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { updateJournal } = useJournalStore();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setName(currentName);
        }
    }, [isOpen, currentName]);

    // Focus and select text when modal opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            // Small timeout to ensure modal is rendered
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        // If name hasn't changed, just close
        if (name.trim() === currentName) {
            onClose();
            return;
        }

        setIsSubmitting(true);
        try {
            await updateJournal(journalId, { name: name.trim() });
            onClose();
        } catch (error: any) {
            console.error('Failed to update journal name', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] transition-opacity"
                        onClick={onClose}
                    />
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto overflow-hidden border border-transparent dark:border-neutral-800"
                        >
                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                        {t('modals.addJournal.journalName')}
                                    </label>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder={t('modals.addJournal.namePlaceholder') || ''}
                                        className="w-full px-4 py-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-accent-primary outline-none transition-all placeholder:text-neutral-400 text-neutral-900 dark:text-neutral-100"
                                        autoComplete="off"
                                        required
                                    />
                                </div>

                                <div className="flex justify-end pt-2">
                                    <button
                                        type="submit"
                                        disabled={!name.trim() || isSubmitting}
                                        className="px-6 py-2.5 bg-accent-primary text-white font-medium rounded-xl hover:bg-accent-primary/90 focus:ring-4 focus:ring-accent-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent-primary/20"
                                    >
                                        {isSubmitting ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
